import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useApi } from "../../hooks/useApi";
import { useStateValue } from "../../context/StateValueContext";
import { CompanyUser, CreateUserResponse, RegeneratePasswordResponse, UsersListResponse, UserRole, CLIENT_ROLES, CLIENT_ROLE_OPTIONS, CLIENT_ROLE_DESCRIPTIONS } from "../../types/companyUser";
import AddAccountButton from "./AddAccountButton";
import { pascalCase } from "../../utils/textUtils";
import { requiresGoogleSignIn } from "../../utils/authUtils";

interface CompanyUsersManagerProps {
  companyId: string;
}

export default function CompanyUsersManager({ companyId }: CompanyUsersManagerProps) {
  const { get, post, patch, loading } = useApi();
  const [{ user: currentUser }] = useStateValue();
  const isChargemontCompany = companyId === "1";
  const userTypeLabel = isChargemontCompany ? "Agent User" : "Client User";

  // Users list state
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState("");
  const [createFirstName, setCreateFirstName] = useState("");
  const [createLastName, setCreateLastName] = useState("");
  const [createRole, setCreateRole] = useState<UserRole>(UserRole.CLIENT_ADMIN);

  // Inline role edit state
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<HTMLElement | null>(null);
  const [editingRoleUserId, setEditingRoleUserId] = useState<number | null>(null);

  // Link existing user dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkApiError, setLinkApiError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);

  // Credentials display state (shown after successful create)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");

  // Status toggle dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [togglingUser, setTogglingUser] = useState<CompanyUser | null>(null);
  const [statusScope, setStatusScope] = useState<'company' | 'all'>('company');

  // Row action menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<CompanyUser | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Regenerate password state
  const [regenConfirmDialogOpen, setRegenConfirmDialogOpen] = useState(false);
  const [regenUser, setRegenUser] = useState<CompanyUser | null>(null);
  const [regenCredentials, setRegenCredentials] = useState<{ email: string; password: string } | null>(null);
  const [regenCopied, setRegenCopied] = useState(false);

  // Shared UI state
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // --- Fetch users ---

  const fetchUsers = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await get<UsersListResponse>(
        `/api/companies/${companyId}/users?page=${page + 1}&limit=${rowsPerPage}`
      );
      setUsers(response.users);
      setTotal(response.total);
    } catch (err: any) {
      console.error("Failed to fetch users:", err);
      setFetchError(err.message || "Failed to load users");
    }
  }, [get, companyId, page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // --- Create user ---

  const handleOpenCreate = () => {
    setCreateEmail("");
    setCreateFirstName("");
    setCreateLastName("");
    setCreateRole(UserRole.CLIENT_ADMIN);
    setCredentials(null);
    setCopied(false);
    setApiError(null);
    setCreateDialogOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateDialogOpen(false);
    setCredentials(null);
    setCopied(false);
    setApiError(null);
    fetchUsers();
  };

  const handleCreateDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") return;
    handleCloseCreate();
  };

  const handleCreateSubmit = async () => {
    if (!createEmail.trim() || !createFirstName.trim() || loading) return;

    try {
      const response = await post<CreateUserResponse>(
        `/api/companies/${companyId}/users`,
        {
          email: createEmail.trim(),
          firstName: createFirstName.trim(),
          lastName: createLastName.trim(),
          role: isChargemontCompany ? "agent" : createRole,
        }
      );
      setCredentials({
        email: response.user.email,
        password: response.temporaryPassword,
      });
      setApiError(null);
      setSnackbar({ open: true, message: "User created successfully", severity: "success" });
    } catch (err: any) {
      setApiError(err.message || "Failed to create user");
    }
  };

  const isGoogleOnlyUser = credentials ? requiresGoogleSignIn(credentials.email) : false;

  const handleCopyCredentials = async () => {
    if (!credentials) return;
    const text = isGoogleOnlyUser
      ? `Email: ${credentials.email}\nSign in with Google`
      : `Email: ${credentials.email}\nTemporary Password: ${credentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setSnackbar({ open: true, message: "Failed to copy to clipboard", severity: "error" });
    }
  };

  // --- Link existing user ---

  const handleOpenLink = () => {
    setLinkEmail("");
    setLinkApiError(null);
    setLinkSuccess(false);
    setLinkDialogOpen(true);
  };

  const handleCloseLink = () => {
    setLinkDialogOpen(false);
    setLinkEmail("");
    setLinkApiError(null);
    if (linkSuccess) {
      fetchUsers();
    }
    setLinkSuccess(false);
  };

  const handleLinkSubmit = async () => {
    if (!linkEmail.trim() || loading) return;

    try {
      await post(`/api/companies/${companyId}/users/link`, {
        email: linkEmail.trim(),
      });
      setLinkSuccess(true);
      setLinkApiError(null);
      setSnackbar({ open: true, message: "User linked successfully", severity: "success" });
      fetchUsers();
    } catch (err: any) {
      setLinkApiError(err.message || "Failed to link user");
    }
  };

  // --- Edit user ---

  const handleOpenEdit = (user: CompanyUser) => {
    setEditingUser(user);
    setEditFirstName(user.first_name);
    setEditLastName(user.last_name);
    setApiError(null);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setApiError(null);
  };

  const handleEditDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") return;
    handleCloseEdit();
  };

  const handleEditSubmit = async () => {
    if (!editingUser || !editFirstName.trim() || loading) return;

    try {
      await patch(
        `/api/users/${editingUser.user_id}`,
        {
          firstName: editFirstName.trim(),
          lastName: editLastName.trim(),
        }
      );
      setSnackbar({ open: true, message: "User updated successfully", severity: "success" });
      handleCloseEdit();
      fetchUsers();
    } catch (err: any) {
      setApiError(err.message || "Failed to update user");
    }
  };

  // --- Status toggle ---

  const handleOpenStatusToggle = (user: CompanyUser, scope: 'company' | 'all') => {
    setTogglingUser(user);
    setStatusScope(scope);
    setApiError(null);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusToggle = () => {
    setStatusDialogOpen(false);
    setTogglingUser(null);
    setApiError(null);
  };

  const handleStatusToggle = async () => {
    if (!togglingUser || loading) return;
    const newStatus = togglingUser.membership_status === "active" ? "disabled" : "active";

    try {
      await patch(
        `/api/users/${togglingUser.user_id}/status`,
        statusScope === 'company'
          ? { status: newStatus, scope: 'company', companyId: Number(companyId) }
          : { status: newStatus, scope: 'all' }
      );
      const scopeLabel = statusScope === 'all' ? ' in all companies' : '';
      setSnackbar({
        open: true,
        message: `User ${newStatus === "active" ? "enabled" : "disabled"}${scopeLabel} successfully`,
        severity: "success",
      });
      handleCloseStatusToggle();
      fetchUsers();
    } catch (err: any) {
      setApiError(err.message || "Failed to update user status");
    }
  };

  // --- Row action menu ---

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, user: CompanyUser) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuUser(null);
  };

  const handleMenuEdit = () => {
    if (menuUser) handleOpenEdit(menuUser);
    handleCloseMenu();
  };

  const handleMenuStatusToggle = (scope: 'company' | 'all') => {
    if (menuUser) handleOpenStatusToggle(menuUser, scope);
    handleCloseMenu();
  };

  // --- Regenerate password ---

  const handleOpenRegenConfirm = () => {
    if (!menuUser) return;
    const user = menuUser;
    handleCloseMenu();
    setRegenUser(user);
    setRegenCredentials(null);
    setRegenCopied(false);
    setApiError(null);
    setRegenConfirmDialogOpen(true);
  };

  const handleCloseRegenConfirm = () => {
    setRegenConfirmDialogOpen(false);
    setRegenUser(null);
    setApiError(null);
  };

  const handleRegeneratePassword = async () => {
    if (!regenUser || loading) return;

    try {
      const response = await post<RegeneratePasswordResponse>(
        `/api/users/${regenUser.user_id}/regenerate-password`,
        {}
      );
      setRegenConfirmDialogOpen(false);
      setRegenCredentials({
        email: response.user.email,
        password: response.temporaryPassword,
      });
      setApiError(null);
      setSnackbar({ open: true, message: "Password regenerated successfully", severity: "success" });
    } catch (err: any) {
      setApiError(err.message || "Failed to regenerate password");
    }
  };

  const handleCloseRegenCredentials = () => {
    setRegenCredentials(null);
    setRegenUser(null);
    setRegenCopied(false);
  };

  const handleCopyRegenCredentials = async () => {
    if (!regenCredentials) return;
    const text = `Email: ${regenCredentials.email}\nTemporary Password: ${regenCredentials.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setRegenCopied(true);
      setTimeout(() => setRegenCopied(false), 2000);
    } catch {
      setSnackbar({ open: true, message: "Failed to copy to clipboard", severity: "error" });
    }
  };

  // --- Inline role change ---

  const handleOpenRoleMenu = (event: React.MouseEvent<HTMLElement>, user: CompanyUser) => {
    setRoleMenuAnchor(event.currentTarget);
    setEditingRoleUserId(user.user_id);
  };

  const handleCloseRoleMenu = () => {
    setRoleMenuAnchor(null);
    setEditingRoleUserId(null);
  };

  const handleRoleChange = async (user: CompanyUser, newRole: UserRole) => {
    handleCloseRoleMenu();
    if (user.role === newRole) return;

    try {
      await patch(
        `/api/users/${user.user_id}/role`,
        { role: newRole }
      );
      setSnackbar({ open: true, message: "Role updated successfully", severity: "success" });
      fetchUsers();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to update role",
        severity: "error",
      });
    }
  };

  // --- Pagination ---

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- Helpers ---

  const formatRole = (role: UserRole) => {
    if (role === UserRole.CLIENT_ADMIN) return "Client Admin";
    if (role === UserRole.CLIENT_USER) return "Client User";
    return pascalCase(role);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-end gap-2">
        {!isChargemontCompany && (
          <AddAccountButton label="Link Existing User" onClick={handleOpenLink} />
        )}
        <AddAccountButton label={`+ ${userTypeLabel}`} onClick={handleOpenCreate} />
      </div>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    Role
                    {!isChargemontCompany && (
                      <Tooltip
                        arrow
                        title={
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
                            {CLIENT_ROLE_OPTIONS.map((r) => (
                              <Box key={r}>
                                <strong>{formatRole(r)}:</strong> {CLIENT_ROLE_DESCRIPTIONS[r]}
                              </Box>
                            ))}
                          </Box>
                        }
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell sx={{ width: 64 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const canEditRole =
                    CLIENT_ROLES.has(user.role) && user.firebase_uid !== currentUser?.uid;
                  return (
                  <TableRow key={user.user_id} hover>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{`${user.first_name} ${user.last_name}`.trim()}</TableCell>
                    <TableCell>
                      {canEditRole ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            "& .role-edit-icon": { opacity: 0 },
                            "&:hover .role-edit-icon": { opacity: 1 },
                          }}
                          onClick={(e) => handleOpenRoleMenu(e, user)}
                        >
                          <Typography variant="body2">{formatRole(user.role)}</Typography>
                          <EditIcon
                            className="role-edit-icon"
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                        </Box>
                      ) : (
                        formatRole(user.role)
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} alignItems="center">
                        <Chip
                          label={user.membership_status === "active" ? "Active" : "Disabled"}
                          size="small"
                          color={user.membership_status === "active" ? "success" : "default"}
                          variant="outlined"
                        />
                        {!user.is_onboarded && (
                          <Chip
                            label="Pending Setup"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(user.last_login_at)}</TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, user)}
                        aria-label="more actions"
                        aria-haspopup="true"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Paper>

      {/* Inline role-change menu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={!!roleMenuAnchor}
        onClose={handleCloseRoleMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {CLIENT_ROLE_OPTIONS.map((role) => {
          const targetUser = users.find((u) => u.user_id === editingRoleUserId);
          return (
            <MenuItem
              key={role}
              selected={targetUser?.role === role}
              onClick={() => targetUser && handleRoleChange(targetUser, role)}
              sx={{ alignItems: "flex-start", py: 1 }}
            >
              <ListItemText
                primary={formatRole(role)}
                secondary={CLIENT_ROLE_DESCRIPTIONS[role]}
                secondaryTypographyProps={{ fontSize: "0.75rem" }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Row action menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleMenuEdit}>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {menuUser && menuUser.role !== UserRole.ADMIN && (
          <MenuItem onClick={() => handleMenuStatusToggle('company')}>
            <ListItemText>
              {menuUser.membership_status === "active"
                ? (menuUser.company_count > 1 ? "Disable in this company" : "Disable")
                : (menuUser.company_count > 1 ? "Enable in this company" : "Enable")}
            </ListItemText>
          </MenuItem>
        )}
        {menuUser && menuUser.role !== UserRole.ADMIN && menuUser.company_count > 1 && (
          <MenuItem onClick={() => handleMenuStatusToggle('all')}>
            <ListItemText>
              {menuUser.membership_status === "active" ? "Disable in all companies" : "Enable in all companies"}
            </ListItemText>
          </MenuItem>
        )}
        {menuUser && !menuUser.is_onboarded && !requiresGoogleSignIn(menuUser.email) && (
          <MenuItem onClick={handleOpenRegenConfirm}>
            <ListItemText>Generate New Password</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create User / Credentials Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {!credentials ? (
          <>
            <DialogTitle>Create {userTypeLabel}</DialogTitle>
            <DialogContent>
              {apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {apiError}
                </Alert>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                  size="small"
                  autoFocus
                />
                <TextField
                  label="First Name"
                  value={createFirstName}
                  onChange={(e) => setCreateFirstName(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                  size="small"
                />
                <TextField
                  label="Last Name"
                  value={createLastName}
                  onChange={(e) => setCreateLastName(e.target.value)}
                  fullWidth
                  disabled={loading}
                  size="small"
                />
                {!isChargemontCompany && (
                  <FormControl fullWidth size="small" disabled={loading}>
                    <InputLabel id="create-role-label">Role</InputLabel>
                    <Select
                      labelId="create-role-label"
                      label="Role"
                      value={createRole}
                      onChange={(e) => setCreateRole(e.target.value as UserRole)}
                      renderValue={(val) => formatRole(val as UserRole)}
                    >
                      {CLIENT_ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r} value={r} sx={{ alignItems: "flex-start", py: 1 }}>
                          <ListItemText
                            primary={formatRole(r)}
                            secondary={CLIENT_ROLE_DESCRIPTIONS[r]}
                            secondaryTypographyProps={{ fontSize: "0.75rem" }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseCreate} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateSubmit}
                variant="contained"
                disabled={loading || !createEmail.trim() || !createFirstName.trim()}
                sx={{
                  backgroundColor: "#465fff",
                  "&:hover": { backgroundColor: "#3641f5" },
                }}
              >
                {loading ? "Creating..." : "Create"}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleOutlineIcon color="success" />
                User Account Created
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                  p: 2,
                  mt: 1,
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email (username)
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                    {credentials.email}
                  </Typography>
                </Box>
                {!isGoogleOnlyUser && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Temporary Password
                    </Typography>
                    <Typography sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {credentials.password}
                    </Typography>
                  </Box>
                )}
              </Box>
              {!isGoogleOnlyUser && (
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyCredentials}
                  sx={{
                    mt: 2,
                    alignSelf: "flex-start",
                    textTransform: "none",
                    borderColor: copied ? "success.main" : undefined,
                    color: copied ? "success.main" : undefined,
                  }}
                >
                  {copied ? "Copied" : "Copy Credentials"}
                </Button>
              )}
              {isGoogleOnlyUser ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This user must sign in using Google with their @chargemont.com email address.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  These are the user's login credentials. The user will be required to change
                  their password upon first sign-in. Please save or share these credentials
                  now — the temporary password cannot be retrieved after closing this dialog.
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseCreate}
                variant="contained"
                sx={{
                  backgroundColor: "#465fff",
                  "&:hover": { backgroundColor: "#3641f5" },
                }}
              >
                Done
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit {userTypeLabel}</DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              value={editingUser?.email || ""}
              fullWidth
              size="small"
              disabled
            />
            <TextField
              label="First Name"
              value={editFirstName}
              onChange={(e) => setEditFirstName(e.target.value)}
              fullWidth
              required
              disabled={loading}
              size="small"
              autoFocus
            />
            <TextField
              label="Last Name"
              value={editLastName}
              onChange={(e) => setEditLastName(e.target.value)}
              fullWidth
              disabled={loading}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEdit} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={loading || !editFirstName.trim()}
            sx={{
              backgroundColor: "#465fff",
              "&:hover": { backgroundColor: "#3641f5" },
            }}
          >
            {loading ? "Saving..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleCloseStatusToggle}>
        <DialogTitle>
          {togglingUser?.membership_status === "active" ? "Disable" : "Enable"} User
          {statusScope === 'all' ? ' in All Companies' : ''}
        </DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Typography>
            Are you sure you want to{" "}
            {togglingUser?.membership_status === "active" ? "disable" : "enable"}{" "}
            <strong>{togglingUser?.email}</strong>
            {statusScope === 'all' ? ' in all companies' : ' in this company'}?
          </Typography>
          {statusScope === 'all' && togglingUser?.company_names && togglingUser.company_names.length > 0 && (
            <Box sx={{ mt: 1.5, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Companies:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {togglingUser.company_names.map((name) => (
                  <li key={name}>
                    <Typography variant="body2">{name}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {togglingUser?.membership_status === "active" && statusScope === 'all' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The user will be signed out and unable to log in to any company until re-enabled.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseStatusToggle} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleStatusToggle}
            variant="contained"
            color={togglingUser?.membership_status === "active" ? "error" : "success"}
            disabled={loading}
          >
            {loading
              ? "Updating..."
              : togglingUser?.membership_status === "active"
                ? "Disable"
                : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Password Confirmation Dialog */}
      <Dialog open={regenConfirmDialogOpen} onClose={handleCloseRegenConfirm}>
        <DialogTitle>Generate New Password</DialogTitle>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Typography>
            Generate a new temporary password for <strong>{regenUser?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The current temporary password will stop working immediately. The user
            will need the new password to sign in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseRegenConfirm} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRegeneratePassword}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#465fff",
              "&:hover": { backgroundColor: "#3641f5" },
            }}
          >
            {loading ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerated Credentials Dialog */}
      <Dialog
        open={!!regenCredentials}
        onClose={(_event: object, reason: string) => {
          if (reason === "backdropClick") return;
          handleCloseRegenCredentials();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleOutlineIcon color="success" />
            New Password Generated
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              backgroundColor: "grey.50",
              borderRadius: 1,
              p: 2,
              mt: 1,
              fontFamily: "monospace",
              fontSize: "0.9rem",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Email (username)
              </Typography>
              <Typography sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                {regenCredentials?.email}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Temporary Password
              </Typography>
              <Typography sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                {regenCredentials?.password}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyRegenCredentials}
            sx={{
              mt: 2,
              alignSelf: "flex-start",
              textTransform: "none",
              borderColor: regenCopied ? "success.main" : undefined,
              color: regenCopied ? "success.main" : undefined,
            }}
          >
            {regenCopied ? "Copied" : "Copy Credentials"}
          </Button>
          <Alert severity="info" sx={{ mt: 2 }}>
            Please save or share these credentials now — the temporary password
            cannot be retrieved after closing this dialog.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseRegenCredentials}
            variant="contained"
            sx={{
              backgroundColor: "#465fff",
              "&:hover": { backgroundColor: "#3641f5" },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Existing User Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={(_event: object, reason: string) => {
          if (reason === "backdropClick") return;
          handleCloseLink();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Existing Client User</DialogTitle>
        <DialogContent>
          {linkApiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {linkApiError}
            </Alert>
          )}
          {linkSuccess ? (
            <Alert severity="success">
              User has been linked to this company successfully.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                Enter the email of an existing Client Admin or Client User to grant them access to this company.
              </Typography>
              <TextField
                label="Email"
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                fullWidth
                required
                disabled={loading}
                size="small"
                autoFocus
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {linkSuccess ? (
            <Button
              onClick={handleCloseLink}
              variant="contained"
              sx={{
                backgroundColor: "#465fff",
                "&:hover": { backgroundColor: "#3641f5" },
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseLink} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleLinkSubmit}
                variant="contained"
                disabled={loading || !linkEmail.trim()}
                sx={{
                  backgroundColor: "#465fff",
                  "&:hover": { backgroundColor: "#3641f5" },
                }}
              >
                {loading ? "Linking..." : "Link User"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}

        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
